import React, { useCallback, useEffect, useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import * as yup from 'yup';
import { debounce } from 'lodash';

import { WizardContent } from '../../components/WizardContent';
import { WizardTitle } from '../../components/WizardTitle';
import WizardButton from '../../components/WizardButton';
import WizardTextField from '../../components/WizardTextField';
import IFormError from '../../../../interfaces/IFormError';
import { Form, Link } from './MedicalData.styles';
import WizardSelect from '../../components/WizardSelect';
import { useWizardRequest } from '../../../../hooks/services/useWizardRequest';
import { useValidateSchema } from '../../../../hooks/providers/useValidateSchema';
import { useToastrVanila } from '../../../../hooks/util/useToastrVanila';
import IProviderService from '../../../../interfaces/IProviderService';

import { useLocalStorage } from '../../../../hooks/providers/useLocalStorage';
import IProviderServiceDataState from '../../../../interfaces/IProviderServiceDataState';

interface MedicalDataProps extends IProviderService {}

const MedicalData = (props: MedicalDataProps) => {
  const [stateProfessional, setStateSelected] = useState('');
  const [registerNumber, setRegisterNumber] = useState('');
  const [professionalName, setProfessionalName] = useState('');
  const [professionalTypeName, setProfessionalTypeName] = useState('');
  const [professionalTypeOrganization, setProfessionalTypeOrganization] = useState('');
  const [states, setStates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<IFormError>();
  const { listProfessionalByRegisterNumber } = useWizardRequest();
  const validationProvader = useValidateSchema();
  const toastr = useToastrVanila();
  const localStorage = useLocalStorage();

  const schema = yup.object({
    stateProfessional: yup
      .string()
      .test((stateProfessional, schema) => stateValidation(`${stateProfessional}`, schema)),
    registerNumber: yup
      .number()
      .typeError('Por favor, insira apenas números neste campo. Outros caracteres não são permitidos.')
      .test((registerNumber, schema) => registerNumberValidation(`${registerNumber}`, schema)),
    professionalName: yup.string().test((professionalName, schema) => nameValidation(`${professionalName}`, schema)),
  });

  const stateValidation = (stateProfessional: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!stateProfessional) {
      return schema.createError({ message: `Campo obrigatório` });
    }
    if (stateProfessional === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }
    return true;
  };

  const registerNumberValidation = (registerNumber: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!registerNumber) {
      return schema.createError({ message: `Campo obrigatório` });
    }

    return true;
  };

  const nameValidation = (professionalName: string, schema: yup.TestContext): yup.ValidationError | boolean => {
    if (!professionalName) {
      return schema.createError({ message: `Campo obrigatório` });
    }

    if (professionalName === 'null') {
      return schema.createError({ message: `Campo obrigatório` });
    }

    return true;
  };

  const setState = (stateProfessional: string) => {
    setStateSelected(stateProfessional);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const result = await validationProvader.validate(schema, { stateProfessional, registerNumber, professionalName });
    setErrors({ ...result.errors });
    if (!result.isValid) {
      return;
    }

    const providerServiceDataState: IProviderServiceDataState = localStorage.getItem('providerServiceDataState');
    const providerServicePageSubmit = 1;

    localStorage.setItem('providerServiceDataState', { ...providerServiceDataState, providerServicePageSubmit });

    await props
      .setStep(props.step + 1, {
        ...props.medicalCertificate,
        professionalTypeId: 1,
        professionalName,
        registerNumber,
        professionalTypeName,
        professionalTypeOrganization,
        professionalState: stateProfessional,
      })
      .then()
      .catch(error => {
        console.log(error);
      });
  };

  const handleListProfessional = async (value: string) => {
    setProfessionalName('');

    if (value.length > 0 && stateProfessional.length > 0) {
      const queryParams = {
        stateProfessional,
        registerNumber: value,
      };
      await listProfessionalByRegisterNumber(queryParams.stateProfessional, queryParams.registerNumber)
        .then(professional => {
          setProfessionalName(professional.name);
          setProfessionalTypeName(`${professional.professionalType?.name}`);
          setProfessionalTypeOrganization(`${professional.professionalType?.organization}`);
          setState(professional.state);
          setLoading(false);
        })
        .catch(error => {
          setLoading(false);
          toastr.error(error.message);
        });
    }
    setLoading(false);
  };

  const debounceProfessional = useCallback(debounce(handleListProfessional, 1000), [stateProfessional]);

  const handleChangeProfessional = async (value: string) => {
    const result = await validationProvader.validate(schema, { value });
    setErrors({ ...result.errors });
    if (!result.isValid) {
      return;
    }

    setLoading(true);
    setRegisterNumber(value);
    await debounceProfessional(value);
  };

  useEffect(() => {
    setStates(props.states);

    setStateSelected(props.medicalCertificate.professionalState);
    setRegisterNumber(props.medicalCertificate.registerNumber);
    setProfessionalName(props.medicalCertificate.professionalName);
  }, []);

  return (
    <WizardContent>
      <WizardTitle>Informe os dados do médico:</WizardTitle>
      <Form onSubmit={handleSubmit}>
        <WizardSelect
          setValueSelected={setState}
          label="Selecione o estado"
          error={Boolean(errors?.stateProfessional)}
          helperText={errors?.registerNumber}
          defaultValueSelected={stateProfessional}
        >
          {states.map(uf => {
            return (
              <MenuItem key={uf} value={uf}>
                {uf}
              </MenuItem>
            );
          })}
        </WizardSelect>

        <WizardTextField
          value={registerNumber}
          onChange={e => {
            handleChangeProfessional(e.target.value);
          }}
          label="CRM"
          sx={{ marginBottom: '20px', width: '300px' }}
          variant="outlined"
          error={Boolean(errors?.registerNumber)}
          helperText={errors?.registerNumber}
        />

        <WizardTextField
          label="Nome do profissional"
          disabled
          value={professionalName}
          onChange={e => {
            setProfessionalName(e.target.value);
          }}
          sx={{ marginBottom: '20px', width: '300px' }}
          variant="outlined"
          error={Boolean(errors?.professionalName)}
          helperText={errors?.professionalName}
        />

        <Link type="button" onClick={() => props.setCurrentPage(2)}>
          Não tenho esses dados
        </Link>

        <WizardButton type="submit" isLoading={loading} disabled={loading} sx={{ marginTop: '35px' }}>
          PRÓXIMO
        </WizardButton>
      </Form>
    </WizardContent>
  );
};

export default MedicalData;
